import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Globe, Menu, Moon, Sun, X } from 'lucide-react';
import { useTheme } from 'next-themes';
import { useLanguage, langPaths } from '@/lib/i18n';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from '@/components/ui/dropdown-menu';

const LOGO_URL = '/logo-transparent.png';

const HOME_PATHS = ['/', '/i', '/deutsch'];

function MobileNav({ t, lang, homePath, switchLang, theme, toggleTheme, mounted, onClose }) {
  return (
    <div className="md:hidden bg-white/95 dark:bg-[#0a0f1f]/95 backdrop-blur-xl border-b border-slate-200/10 dark:border-white/10 px-6 py-6 space-y-4 animate-fade-in">
      <Link to={homePath} onClick={onClose} className="block text-slate-900 dark:text-white font-medium" data-testid="mobile-home-link">
        {t.nav.home}
      </Link>
      <Link to="/contact" onClick={onClose} className="block text-slate-900 dark:text-white font-medium" data-testid="mobile-contact-link">
        {t.nav.contact}
      </Link>
      <div className="flex gap-3 pt-2">
        {['es', 'en', 'de'].map((l) => (
          <button
            key={l}
            onClick={() => { switchLang(l); onClose(); }}
            className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all ${lang === l ? 'bg-[#1a6cf5] text-white' : 'text-gray-500 dark:text-gray-400 border border-slate-200/10 dark:border-white/10'}`}
            data-testid={`mobile-lang-${l}`}
          >
            {l.toUpperCase()}
          </button>
        ))}
      </div>
      <button
        type="button"
        onClick={() => { toggleTheme(); onClose(); }}
        className="w-full inline-flex items-center justify-center gap-2 rounded-full border border-slate-200/10 dark:border-white/10 bg-slate-100/80 dark:bg-white/5 px-4 py-2 text-sm font-medium text-slate-900 dark:text-white hover:bg-slate-200/80 dark:hover:bg-white/10 transition-colors"
        data-testid="mobile-theme-btn"
      >
        {mounted && theme === 'dark' ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
        {mounted ? (theme === 'dark' ? t.nav.day : t.nav.night) : t.nav.themeToggle}
      </button>
      <a
        href="https://app.skyboard.es"
        target="_blank"
        rel="noopener noreferrer"
        className="block text-center bg-[#1a6cf5] text-white rounded-full px-6 py-2.5 text-sm font-medium mt-2"
        data-testid="mobile-simulator-btn"
      >
        {t.nav.simulator}
      </a>
    </div>
  );
}

export default function Navbar() {
  const { lang, setLang, t } = useLanguage();
  const { theme, setTheme } = useTheme();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mounted, setMounted] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const homePath = langPaths[lang];
  const isActive = (path) =>
    HOME_PATHS.includes(path) ? HOME_PATHS.includes(location.pathname) : location.pathname === path;
  const navLinkClass = (path) =>
    `text-sm font-medium transition-colors ${isActive(path) ? 'dark:text-white text-slate-900' : 'dark:text-gray-400 text-slate-500 hover:dark:text-white hover:text-slate-900'}`;

  useEffect(() => {
    setMounted(true);
  }, []);

  const switchLang = (l) => {
    setLang(l);
    navigate(langPaths[l]);
  };

  const toggleTheme = () => {
    setTheme(theme === 'dark' ? 'light' : 'dark');
  };

  return (
    <header className="fixed top-0 w-full z-50 backdrop-blur-xl bg-white/80 dark:bg-[#0a0f1f]/80 border-b border-slate-200/60 dark:border-white/10" data-testid="navbar">
      <nav className="max-w-7xl mx-auto px-6 lg:px-8 flex items-center justify-between h-16">
        <Link to={homePath} className="flex items-center gap-3" data-testid="navbar-logo">
          <img src={LOGO_URL} alt="Skyboard" className="h-[94px] w-auto" />
        </Link>

        <div className="hidden md:flex items-center gap-8">
          <Link to={homePath} className={`${navLinkClass(homePath)} nav-link-hover`} data-testid="nav-home-link">
            {t.nav.home}
          </Link>
          <Link to="/contact" className={`${navLinkClass('/contact')} nav-link-hover`} data-testid="nav-contact-link">
            {t.nav.contact}
          </Link>

            <DropdownMenu>
            <DropdownMenuTrigger
  className="flex items-center gap-1.5 text-sm text-slate-500 dark:text-gray-400 hover:text-slate-900 dark:hover:text-white transition-colors outline-none cursor-pointer"
  data-testid="language-selector"
>
              <Globe className="h-4 w-4" />
              {lang.toUpperCase()}
            </DropdownMenuTrigger>
            <DropdownMenuContent className="bg-white dark:bg-[#121b33] border border-slate-200 dark:border-white/10 min-w-[120px]">
              <DropdownMenuItem onClick={() => switchLang('es')} className="text-slate-900 dark:text-white hover:bg-slate-100 dark:hover:bg-white/10 cursor-pointer" data-testid="lang-es">
                Español
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => switchLang('en')} className="text-slate-900 dark:text-white hover:bg-slate-100 dark:hover:bg-white/10 cursor-pointer" data-testid="lang-en">
                English
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => switchLang('de')} className="text-slate-900 dark:text-white hover:bg-slate-100 dark:hover:bg-white/10 cursor-pointer" data-testid="lang-de">
                Deutsch
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          <button
            type="button"
            onClick={toggleTheme}
            className="inline-flex items-center justify-center rounded-full border border-slate-200/10 dark:border-white/10 bg-slate-100/80 dark:bg-white/5 p-2 text-slate-900 dark:text-white hover:bg-slate-200/80 dark:hover:bg-white/10 transition-colors"
            data-testid="theme-toggle-btn"
            aria-label={t.nav.themeToggle}
          >
            {mounted && theme === 'dark' ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
          </button>

          <a
            href="https://app.skyboard.es"
            target="_blank"
            rel="noopener noreferrer"
            className="bg-[#1a6cf5] text-white rounded-full px-6 py-2 text-sm font-medium hover:bg-[#3b82f6] transition-all btn-glow"
            data-testid="nav-simulator-btn"
          >
            {t.nav.simulator}
          </a>
        </div>

        <button
  className="md:hidden text-slate-900 dark:text-white p-2"
  onClick={() => setMobileOpen(!mobileOpen)}
  data-testid="mobile-menu-btn"
>
          {mobileOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </nav>

      {mobileOpen && (
        <MobileNav
          t={t}
          lang={lang}
          homePath={homePath}
          switchLang={switchLang}
          theme={theme}
          toggleTheme={toggleTheme}
          mounted={mounted}
          onClose={() => setMobileOpen(false)}
        />
      )}
    </header>
  );
}
